import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BrandedCard, BootPulse } from '../components';
import { kratosService, KratosErrorPayload } from '../services/kratosService';
import './FlowError.css';

/**
 * /error — renders the Ory self-service error payload Kratos redirects to
 * when a flow fails in a way it can't show inline (`/self-service/errors?id=`).
 */
export const FlowError: React.FC = () => {
  const [searchParams] = useSearchParams();
  const errorId = searchParams.get('id');

  const [payload, setPayload] = useState<KratosErrorPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!errorId) {
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const data = await kratosService.getFlowError(errorId);
        setPayload(data);
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Unable to load error details.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [errorId]);

  if (loading) {
    return (
      <BrandedCard>
        <BootPulse label="Loading error details" />
      </BrandedCard>
    );
  }

  const message =
    payload?.error?.message ||
    payload?.error?.reason ||
    loadError ||
    'Something went wrong. Please try again.';

  return (
    <BrandedCard title="Something Went Wrong">
      <div className="flow-error-banner" role="alert">
        {message}
      </div>

      {payload?.error?.id && <div className="flow-error-code">Reference: {payload.error.id}</div>}

      <div className="flow-error-links">
        <a href="/login" className="flow-error-link">
          Back to sign in
        </a>
      </div>
    </BrandedCard>
  );
};
